import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { theme, card, floatingSheet, btnPrimary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { PortalLink } from "./PortalLink";

// Directory role sync settings — the same panel serves a client organisation
// (Team page, scope "org") and a consultancy (consultant settings, scope "firm").
// Membership and attributes come from Entra ID through the M365 Security
// connector; on-premises AD reaches Entra ID via Entra Connect.

type Scope = "org" | "firm";

interface Config {
  enabled: boolean;
  syncDepartments: boolean;
  syncProfile: boolean;
  syncTags: boolean;
  unmatchedAction: "demote" | "keep";
  deactivateDisabled: boolean;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  lastSyncSummary: Summary | null;
}

interface Summary {
  ranAt: string;
  directoryUsers: number;
  members: number;
  matched: number;
  notInDirectory: number;
  roleChanges: Array<{ email: string; from: string | null; to: string; source: string }>;
  departmentChanges: Array<{ email: string; from: string | null; to: string | null }>;
  siteChanges?: Array<{ email: string; from: string | null; to: string | null }>;   // absent on summaries stored before sites existed
  tagChanges?: Array<{ email: string; kind: "office" | "country"; from: string | null; to: string | null }>;   // absent before tags were synced
  profileUpdates: number;
  deactivated: string[];
  skipped: Array<{ email: string; reason: string }>;
  warnings: string[];
}

interface Rule {
  id?: string;
  matchKind: "group" | "department";
  matchValue: string;
  matchLabel: string | null;
  target: string;   // orgRoleId (org) or consultant role key (firm)
}

interface Target { key: string; name: string; color?: string; isSystem?: boolean; isAdmin: boolean; }

interface Connector { connected: boolean; organizationId: string | null; organizationName: string | null; tenantId: string | null; }

interface PanelData {
  scope: "organization" | "consultancy";
  config: Config;
  rules: Array<{ id: string; position: number; matchKind: "group" | "department"; matchValue: string; matchLabel: string | null; orgRoleId: string | null; consultantRole: string | null }>;
  connector: Connector;
  targets: Target[];
}

interface GroupHit { id: string; displayName: string; description: string | null; securityEnabled: boolean; mailEnabled: boolean; onPremisesSyncEnabled: boolean; }

const sectionTitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: theme.color.heading, margin: "0 0 4px" };
const sectionHint: React.CSSProperties = { fontSize: 12, color: theme.color.subtle, margin: "0 0 12px", lineHeight: 1.5 };
const labelStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, fontWeight: 600 };
const smallBtn: React.CSSProperties = {
  padding: "4px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`,
  background: "transparent", color: theme.color.text, fontSize: 12, cursor: "pointer",
};
const secondaryBtn: React.CSSProperties = { ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" };

function CheckRow({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint: string; disabled?: boolean }) {
  return (
    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.text }}>{label}</span>
        <span style={{ display: "block", fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{hint}</span>
      </span>
    </label>
  );
}

function formatWhen(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// ── Group picker ──────────────────────────────────────────────────────────────

function GroupPicker({ scope, value, label, onPick }: { scope: Scope; value: string; label: string | null; onPick: (g: GroupHit) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<GroupHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef<number | null>(null);

  const search = useCallback((query: string) => {
    setLoading(true); setError("");
    api.get<{ groups: GroupHit[] }>(`/directory-sync/${scope}/groups`, { q: query })
      .then((r) => setHits(r.groups))
      .catch((e) => setError(apiErrorMessage(e, "Could not search the directory")))
      .finally(() => setLoading(false));
  }, [scope]);

  useEffect(() => {
    if (!open) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => search(q), 300);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [open, q, search]);

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ ...inputStyle, textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: value ? theme.color.text : theme.color.subtle }}>
          {value ? (label ?? value) : "Choose a group…"}
        </span>
        <span style={{ color: theme.color.subtle }}>▾</span>
      </button>
      {open && (
        <div style={{ ...floatingSheet, position: "absolute", zIndex: 20, top: "100%", left: 0, right: 0, marginTop: 4, padding: 8, maxHeight: 280, overflowY: "auto" }}>
          <input autoFocus style={{ ...inputStyle, marginBottom: 6 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search groups by name…" />
          {loading && <div style={{ fontSize: 12, color: theme.color.subtle, padding: 6 }}>Searching…</div>}
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText, padding: 6 }}>{error}</div>}
          {!loading && !error && hits.length === 0 && <div style={{ fontSize: 12, color: theme.color.subtle, padding: 6 }}>No groups found.</div>}
          {!loading && hits.map((g) => (
            <button
              key={g.id} type="button"
              onClick={() => { onPick(g); setOpen(false); setQ(""); }}
              style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", padding: "6px 8px", borderRadius: 3, cursor: "pointer", color: theme.color.text }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>{g.displayName}</div>
              <div style={{ fontSize: 11, color: theme.color.subtle }}>
                {[g.securityEnabled ? "Security group" : g.mailEnabled ? "Mail-enabled group" : "Group", g.onPremisesSyncEnabled ? "synced from on-premises AD" : null, g.description].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export function DirectorySyncPanel({ scope }: { scope: Scope }) {
  const [data, setData] = useState<PanelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [config, setConfig] = useState<Config | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const load = useCallback(() => {
    setLoadError("");
    return api.get<PanelData>(`/directory-sync/${scope}`)
      .then((d) => {
        setData(d);
        setConfig(d.config);
        setRules(d.rules.map((r) => ({ id: r.id, matchKind: r.matchKind, matchValue: r.matchValue, matchLabel: r.matchLabel, target: (d.scope === "organization" ? r.orgRoleId : r.consultantRole) ?? "" })));
      })
      .catch((e) => setLoadError(apiErrorMessage(e, "Failed to load directory sync settings")))
      .finally(() => setLoading(false));
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  // Poll while a run is in flight so the status card updates without a reload.
  useEffect(() => {
    if (!running) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      api.get<PanelData>(`/directory-sync/${scope}`).then((d) => {
        setData((prev) => prev ? { ...prev, config: d.config, rules: d.rules } : d);
        if (d.config.lastSyncAt && new Date(d.config.lastSyncAt).getTime() >= started) {
          setConfig((c) => c ? { ...c, lastSyncAt: d.config.lastSyncAt, lastSyncError: d.config.lastSyncError, lastSyncSummary: d.config.lastSyncSummary } : c);
          setRunning(false);
        }
      }).catch(() => { /* keep polling */ });
      if (Date.now() - started > 3 * 60 * 1000) setRunning(false);
    }, 4000);
    return () => window.clearInterval(id);
  }, [running, scope]);

  const targets = data?.targets ?? [];
  const targetName = useCallback((key: string) => targets.find((t) => t.key === key)?.name ?? key, [targets]);

  const settingsDirty = useMemo(() => {
    if (!config || !data) return false;
    const a = data.config, b = config;
    return a.enabled !== b.enabled || a.syncDepartments !== b.syncDepartments || a.syncProfile !== b.syncProfile || a.syncTags !== b.syncTags
      || a.unmatchedAction !== b.unmatchedAction || a.deactivateDisabled !== b.deactivateDisabled;
  }, [config, data]);

  const rulesDirty = useMemo(() => {
    if (!data) return false;
    const saved = data.rules.map((r) => `${r.matchKind}|${r.matchValue}|${(data.scope === "organization" ? r.orgRoleId : r.consultantRole) ?? ""}`);
    const current = rules.map((r) => `${r.matchKind}|${r.matchValue}|${r.target}`);
    return saved.join("\n") !== current.join("\n");
  }, [rules, data]);

  async function saveSettings() {
    if (!config) return;
    setSavingSettings(true); setNotice(null);
    try {
      const res = await api.put<{ config: Config }>(`/directory-sync/${scope}`, {
        enabled: config.enabled, syncDepartments: config.syncDepartments, syncProfile: config.syncProfile, syncTags: config.syncTags,
        unmatchedAction: config.unmatchedAction, deactivateDisabled: config.deactivateDisabled,
      });
      setConfig(res.config);
      setData((d) => d ? { ...d, config: res.config } : d);
      setNotice({ kind: "ok", text: res.config.enabled ? "Settings saved — a sync has been queued." : "Settings saved." });
      if (res.config.enabled) setRunning(true);
    } catch (e) {
      setNotice({ kind: "error", text: apiErrorMessage(e, "Failed to save settings") });
    } finally {
      setSavingSettings(false);
    }
  }

  async function saveRules() {
    setSavingRules(true); setNotice(null);
    try {
      const body = rules.map((r) => ({
        matchKind: r.matchKind,
        matchValue: r.matchValue,
        matchLabel: r.matchLabel,
        ...(scope === "org" ? { orgRoleId: r.target } : { consultantRole: r.target }),
      }));
      const res = await api.put<{ rules: PanelData["rules"] }>(`/directory-sync/${scope}/rules`, { rules: body });
      setData((d) => d ? { ...d, rules: res.rules } : d);
      setRules(res.rules.map((r) => ({ id: r.id, matchKind: r.matchKind, matchValue: r.matchValue, matchLabel: r.matchLabel, target: (scope === "org" ? r.orgRoleId : r.consultantRole) ?? "" })));
      setNotice({ kind: "ok", text: config?.enabled ? "Rules saved — a sync has been queued." : "Rules saved." });
      if (config?.enabled) setRunning(true);
    } catch (e) {
      setNotice({ kind: "error", text: apiErrorMessage(e, "Failed to save rules") });
    } finally {
      setSavingRules(false);
    }
  }

  async function runNow() {
    setNotice(null);
    try {
      await api.post(`/directory-sync/${scope}/run`, {});
      setRunning(true);
      setNotice({ kind: "ok", text: "Sync queued — results appear below when it finishes." });
    } catch (e) {
      setNotice({ kind: "error", text: apiErrorMessage(e, "Failed to start a sync") });
    }
  }

  function updateRule(i: number, patch: Partial<Rule>) {
    setRules((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }
  function moveRule(i: number, delta: number) {
    setRules((rs) => {
      const j = i + delta;
      if (j < 0 || j >= rs.length) return rs;
      const copy = [...rs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }

  if (loading) return <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>;
  if (loadError || !data || !config) {
    return (
      <div style={{ ...card, display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontSize: 13, color: theme.color.dangerText }}>{loadError || "Failed to load directory sync settings"}</span>
        <button type="button" onClick={() => { setLoading(true); load(); }} style={smallBtn}>Retry</button>
      </div>
    );
  }

  const isOrg = data.scope === "organization";
  const connector = data.connector;
  const summary = config.lastSyncSummary;
  const rulesValid = rules.every((r) => r.matchValue.trim() && r.target);
  const baselineName = isOrg ? "Viewer" : "Report writer";
  const peopleWord = isOrg ? "members" : "staff";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <h2 style={sectionTitle}>Roles follow your directory</h2>
        <p style={sectionHint}>
          Assign access where you already manage it: put people in Active Directory or Entra ID security groups and
          Abacas gives each matching {isOrg ? "member" : "staff member"} the role you map here, every hour and whenever you
          press Sync now. {isOrg ? "The directory's department attribute places members in the matching department, job title and office location are copied to their profile, and members are tagged with their office and country." : "Job title and office location are copied from the directory."}
          {" "}On-premises groups and attributes reach Entra ID through Entra Connect; Group Policy itself never leaves the domain, so
          a role is granted by group membership, not by a GPO.
        </p>
      </div>

      {/* Connector */}
      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
            {connector.connected ? "Reading the directory through the Microsoft 365 Security connector" : "Microsoft 365 Security connector not connected"}
          </div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
            {connector.connected
              ? `${connector.organizationName ?? "Organisation"} · tenant ${connector.tenantId ?? "unknown"} · uses the app registration's Directory.Read.All permission`
              : isOrg
                ? "Connect it under Connections → Microsoft 365 Security. The same app registration (Directory.Read.All) is used to read groups and users."
                : connector.organizationName
                  ? `Connect Microsoft 365 Security on ${connector.organizationName} (your own organisation's portal → Connections).`
                  : "Register your consultancy's own organisation first; the directory is read through its Microsoft 365 Security connector."}
          </div>
        </div>
        {!connector.connected && isOrg && (
          <PortalLink to="/connections" style={{ ...secondaryBtn, textDecoration: "none", fontSize: 12 }}>Open Connections</PortalLink>
        )}
      </div>

      {/* Settings */}
      <div style={card}>
        <h3 style={sectionTitle}>Settings</h3>
        <p style={sectionHint}>Sync only ever changes existing {peopleWord}; nobody is created from the directory and access stays invite-only.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <CheckRow
            checked={config.enabled} onChange={(v) => setConfig({ ...config, enabled: v })}
            label="Enable directory sync" disabled={!connector.connected && !config.enabled}
            hint={connector.connected ? "Runs every hour on the worker. Turning it off leaves roles as they are." : "Connect the Microsoft 365 Security connector first."}
          />
          <div>
            <div style={{ ...labelStyle, marginBottom: 6 }}>{isOrg ? "Members" : "Staff"} in the directory who match no rule</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {([
                ["demote", `Drop to ${baselineName}`, `Leavers and movers lose their rights within the hour. ${isOrg ? "Members" : "Staff"} the directory does not know at all are never touched.`],
                ["keep", "Keep their current role", "Only matched rules assign roles; everyone else stays as set by hand."],
              ] as const).map(([value, label, hint]) => (
                <label key={value} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                  <input type="radio" name={`unmatched-${scope}`} checked={config.unmatchedAction === value} onChange={() => setConfig({ ...config, unmatchedAction: value })} style={{ marginTop: 3 }} />
                  <span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.text }}>{label}</span>
                    <span style={{ display: "block", fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <CheckRow
            checked={config.deactivateDisabled} onChange={(v) => setConfig({ ...config, deactivateDisabled: v })}
            label="Deactivate accounts that are disabled in the directory"
            hint={`A disabled AD / Entra account is deactivated here and signed out everywhere. Re-activation is manual. ${isOrg ? "The organisation's last administrator" : "The consultancy's last lead consultant"} is never deactivated.`}
          />
          {isOrg && (
            <CheckRow
              checked={config.syncDepartments} onChange={(v) => setConfig({ ...config, syncDepartments: v })}
              label="Place members in the department named by the directory"
              hint="Matches the department attribute to a department by name and creates it if missing. Manually added departments are kept; only the directory's one moves."
            />
          )}
          <CheckRow
            checked={config.syncProfile} onChange={(v) => setConfig({ ...config, syncProfile: v })}
            label="Copy job title and office location"
            hint="Directory values overwrite the profile fields only when the directory has a value. An office location that matches a site by name also places the member at that site; sites are never created by the sync."
          />
          {isOrg && (
            <CheckRow
              checked={config.syncTags} onChange={(v) => setConfig({ ...config, syncTags: v })}
              label="Tag members with their office and country"
              hint="Applies a tag named after the office (officeLocation) and one named after the country (country, else usageLocation; codes such as JE become Jersey). Tags are created when missing and swapped when the directory changes; tags you applied by hand are never touched."
            />
          )}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 16 }}>
          <button type="button" onClick={saveSettings} disabled={!settingsDirty || savingSettings} style={{ ...btnPrimary, opacity: !settingsDirty || savingSettings ? 0.6 : 1 }}>
            {savingSettings ? "Saving…" : "Save settings"}
          </button>
          {settingsDirty && <span style={{ fontSize: 12, color: theme.color.subtle }}>Unsaved changes</span>}
        </div>
      </div>

      {/* Rules */}
      <div style={card}>
        <h3 style={sectionTitle}>Rules</h3>
        <p style={sectionHint}>
          Checked top to bottom; the first rule that matches decides the role, so put the most privileged groups first.
          A rule matches on membership of a security group (including nested groups) or on the directory's department attribute.
          {isOrg ? " Framework Specialist cannot be assigned by sync because it needs a per-person framework list." : ""}
        </p>

        {rules.length === 0 && (
          <div style={{ fontSize: 12, color: theme.color.subtle, padding: "8px 0" }}>
            No rules yet. {config.unmatchedAction === "demote" && config.enabled ? `With no rules, every matched ${isOrg ? "member" : "staff member"} drops to ${baselineName}.` : ""}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rules.map((rule, i) => (
            <div key={rule.id ?? `new-${i}`} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: 10, borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panelAlt }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, width: 22, textAlign: "center" }}>{i + 1}</span>
              <select style={{ ...inputStyle, width: "auto" }} value={rule.matchKind} onChange={(e) => updateRule(i, { matchKind: e.target.value as Rule["matchKind"], matchValue: "", matchLabel: null })}>
                <option value="group">Security group</option>
                <option value="department">Department attribute</option>
              </select>
              {rule.matchKind === "group" ? (
                <GroupPicker scope={scope} value={rule.matchValue} label={rule.matchLabel} onPick={(g) => updateRule(i, { matchValue: g.id, matchLabel: g.displayName })} />
              ) : (
                <input style={{ ...inputStyle, flex: 1, minWidth: 200 }} value={rule.matchValue} maxLength={80} onChange={(e) => updateRule(i, { matchValue: e.target.value })} placeholder="Department exactly as in the directory, e.g. IT" />
              )}
              <span style={{ fontSize: 12, color: theme.color.subtle }}>→</span>
              <select style={{ ...inputStyle, width: "auto", minWidth: 160 }} value={rule.target} onChange={(e) => updateRule(i, { target: e.target.value })}>
                <option value="">Choose a role…</option>
                {targets.map((t) => <option key={t.key} value={t.key}>{t.name}{t.isAdmin ? " (admin)" : ""}</option>)}
              </select>
              <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
                <button type="button" onClick={() => moveRule(i, -1)} disabled={i === 0} style={{ ...smallBtn, opacity: i === 0 ? 0.4 : 1 }} aria-label="Move up">↑</button>
                <button type="button" onClick={() => moveRule(i, 1)} disabled={i === rules.length - 1} style={{ ...smallBtn, opacity: i === rules.length - 1 ? 0.4 : 1 }} aria-label="Move down">↓</button>
                <button type="button" onClick={() => setRules((rs) => rs.filter((_, idx) => idx !== i))} style={{ ...smallBtn, color: theme.color.danger }}>Remove</button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <button type="button" onClick={() => setRules((rs) => [...rs, { matchKind: "group", matchValue: "", matchLabel: null, target: "" }])} disabled={rules.length >= 50} style={secondaryBtn}>
            + Add rule
          </button>
          <button type="button" onClick={saveRules} disabled={!rulesDirty || !rulesValid || savingRules} style={{ ...btnPrimary, opacity: !rulesDirty || !rulesValid || savingRules ? 0.6 : 1 }}>
            {savingRules ? "Saving…" : "Save rules"}
          </button>
          {rulesDirty && !rulesValid && <span style={{ fontSize: 12, color: theme.color.dangerText }}>Every rule needs a group or department and a role.</span>}
          {rulesDirty && rulesValid && <span style={{ fontSize: 12, color: theme.color.subtle }}>Unsaved changes</span>}
        </div>
      </div>

      {/* Status */}
      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h3 style={sectionTitle}>Last sync</h3>
            <div style={{ fontSize: 12, color: theme.color.subtle }}>
              {running ? "Running…" : formatWhen(config.lastSyncAt)}
              {config.lastSyncError && !running && <span style={{ color: theme.color.dangerText }}> · {config.lastSyncError}</span>}
            </div>
          </div>
          <button type="button" onClick={runNow} disabled={!data.config.enabled || running} style={{ ...secondaryBtn, opacity: !data.config.enabled || running ? 0.6 : 1 }}>
            {running ? "Syncing…" : "Sync now"}
          </button>
        </div>

        {summary && !running && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: theme.color.text }}>
              <span><strong>{summary.matched}</strong> of {summary.members} {peopleWord} found in the directory ({summary.directoryUsers} directory accounts)</span>
              <span><strong>{summary.roleChanges.length}</strong> role change{summary.roleChanges.length === 1 ? "" : "s"}</span>
              {isOrg && <span><strong>{summary.departmentChanges.length}</strong> department change{summary.departmentChanges.length === 1 ? "" : "s"}</span>}
              {isOrg && (summary.siteChanges ?? []).length > 0 && <span><strong>{summary.siteChanges!.length}</strong> site change{summary.siteChanges!.length === 1 ? "" : "s"}</span>}
              {isOrg && (summary.tagChanges ?? []).length > 0 && <span><strong>{summary.tagChanges!.length}</strong> tag change{summary.tagChanges!.length === 1 ? "" : "s"}</span>}
              <span><strong>{summary.profileUpdates}</strong> profile update{summary.profileUpdates === 1 ? "" : "s"}</span>
              <span><strong>{summary.deactivated.length}</strong> deactivated</span>
              {summary.skipped.length > 0 && <span style={{ color: theme.color.warningText }}><strong>{summary.skipped.length}</strong> skipped</span>}
              {summary.warnings.length > 0 && <span style={{ color: theme.color.warningText }}><strong>{summary.warnings.length}</strong> warning{summary.warnings.length === 1 ? "" : "s"}</span>}
            </div>
            {(summary.roleChanges.length + summary.departmentChanges.length + (summary.siteChanges ?? []).length + (summary.tagChanges ?? []).length + summary.deactivated.length + summary.skipped.length + summary.warnings.length) > 0 && (
              <button type="button" onClick={() => setShowDetails((s) => !s)} style={{ ...smallBtn, marginTop: 10 }}>{showDetails ? "Hide details" : "Show details"}</button>
            )}
            {showDetails && (
              <div style={{ marginTop: 10, fontSize: 12, color: theme.color.text, display: "flex", flexDirection: "column", gap: 10 }}>
                {summary.warnings.length > 0 && (
                  <div>
                    <div style={labelStyle}>Warnings</div>
                    {summary.warnings.map((w, i) => <div key={i} style={{ color: theme.color.warningText }}>{w}</div>)}
                  </div>
                )}
                {summary.roleChanges.length > 0 && (
                  <div>
                    <div style={labelStyle}>Role changes</div>
                    {summary.roleChanges.map((c, i) => <div key={i}>{c.email}: {c.from ?? "no role"} → {c.to} <span style={{ color: theme.color.subtle }}>({c.source})</span></div>)}
                  </div>
                )}
                {summary.departmentChanges.length > 0 && (
                  <div>
                    <div style={labelStyle}>Department changes</div>
                    {summary.departmentChanges.map((c, i) => <div key={i}>{c.email}: {c.from ?? "none"} → {c.to ?? "none"}</div>)}
                  </div>
                )}
                {(summary.siteChanges ?? []).length > 0 && (
                  <div>
                    <div style={labelStyle}>Site changes</div>
                    {summary.siteChanges!.map((c, i) => <div key={i}>{c.email}: {c.from ?? "none"} → {c.to ?? "none"}</div>)}
                  </div>
                )}
                {(summary.tagChanges ?? []).length > 0 && (
                  <div>
                    <div style={labelStyle}>Tag changes</div>
                    {summary.tagChanges!.map((c, i) => <div key={i}>{c.email}: {c.kind} {c.from ?? "none"} → {c.to ?? "none"}</div>)}
                  </div>
                )}
                {summary.deactivated.length > 0 && (
                  <div>
                    <div style={labelStyle}>Deactivated</div>
                    {summary.deactivated.map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                )}
                {summary.skipped.length > 0 && (
                  <div>
                    <div style={labelStyle}>Skipped</div>
                    {summary.skipped.map((s, i) => <div key={i}>{s.email}: <span style={{ color: theme.color.subtle }}>{s.reason}</span></div>)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {notice && (
        <div style={{ fontSize: 13, color: notice.kind === "ok" ? theme.color.successText : theme.color.dangerText }}>{notice.text}</div>
      )}

      <div style={{ fontSize: 11, color: theme.color.subtle, lineHeight: 1.5 }}>
        <strong>How matching works.</strong> {isOrg ? "Members" : "Staff"} who signed in with Microsoft are matched on their directory object id; everyone else on email
        against the user principal name or mail attribute. Every role change signs the person out of existing sessions and is written to the activity log.
        Roles set by sync are marked on the {isOrg ? "Members" : "team"} list; a manual change is re-applied by the next run while sync stays enabled.
      </div>
    </div>
  );
}
